// Api
import InventoryApi from '@/api/InventoryApi';

// Utils
import ErrorUtil from '@/utils/ErrorUtil';

import modules from './modules';

export const getterTypes = {
    hasPossiblePartners: 'hasPossiblePartners',
    hasPartneredLocations: 'hasPartneredLocations',
};

export const actionTypes = {
    getAllPossiblePartners: 'getAllPossiblePartners',
    getPartneredLocations: 'getPartneredLocations',
    updatePartneredLocations: 'updatePartneredLocations',
    reset: 'reset',
};

export const mutationTypes = {
    SET_POSSIBLE_PARTNERS_LIST: 'SET_POSSIBLE_PARTNERS_LIST',
    SET_POSSIBLE_PARTNERS_IN_FLIGHT: 'SET_POSSIBLE_PARTNERS_IN_FLIGHT',
    SET_PARTNERED_LOCATIONS_LIST: 'SET_PARTNERED_LOCATIONS_LIST',
    SET_PARTNERED_LOCATIONS_IN_FLIGHT: 'SET_PARTNERED_LOCATIONS_IN_FLIGHT',
};

export default {
    namespaced: true,
    modules,
    state: {
        possiblePartners: {
            list: [],
            inFlight: false,
        },
        partneredLocations: {
            list: [],
            inFlight: false,
        },
    },
    getters: {
        /** @returns { Boolean } */
        [ getterTypes.hasPossiblePartners ]: ( state ) => (
            state.possiblePartners.list.length > 0
        ),
        /** @returns { Boolean } */
        [ getterTypes.hasPartneredLocations ]: ( state ) => (
            state.partneredLocations.list.length > 0
        ),
        /** @returns { Object } */
        partnerList: ( state, getters, rootState ) => ( state.partneredLocations.list.filter( ( item ) => item.id !== rootState.Website.info.locationId ) ),
    },
    actions: {
        [ actionTypes.reset ]( { commit } ) {
            commit( mutationTypes.SET_POSSIBLE_PARTNERS_LIST, { list: [] } );
            commit( mutationTypes.SET_POSSIBLE_PARTNERS_IN_FLIGHT, { inFlight: false } );
            commit( mutationTypes.SET_PARTNERED_LOCATIONS_LIST, { list: [] } );
            commit( mutationTypes.SET_PARTNERED_LOCATIONS_IN_FLIGHT, { inFlight: false } );
        },

        async [ actionTypes.getAllPossiblePartners ]( context ) {
            context.commit( mutationTypes.SET_POSSIBLE_PARTNERS_IN_FLIGHT, { inFlight: true } );
            try {
                const { data } = await InventoryApi.getPartnerList( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                } );
                context.commit( mutationTypes.SET_POSSIBLE_PARTNERS_LIST, {
                    list: data.locations.sort( ( a, b ) => ( a.friendly > b.friendly ? 1 : -1 ) ),
                } );
            } catch ( error ) {
                const message = `An error occurred while getting all possible partner locations: ${ ErrorUtil.getMessageFromError( error ) }`;
                await context.dispatch( 'snackbar', {
                    message,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                throw new Error( ErrorUtil.getMessageFromError( error ) );
            } finally {
                context.commit( mutationTypes.SET_POSSIBLE_PARTNERS_IN_FLIGHT, { inFlight: false } );
            }
        },

        async [ actionTypes.getPartneredLocations ]( context ) {
            context.commit( mutationTypes.SET_PARTNERED_LOCATIONS_IN_FLIGHT, { inFlight: true } );
            try {
                const { data } = await InventoryApi.getLocationPartnerList( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                } );
                context.commit( mutationTypes.SET_PARTNERED_LOCATIONS_LIST, {
                    list: data.locations.sort( ( a, b ) => ( a.friendly > b.friendly ? 1 : -1 ) ),
                } );
            } catch ( error ) {
                const message = `An error occurred while getting partnered locations: ${ ErrorUtil.getMessageFromError( error ) }`;
                await context.dispatch( 'snackbar', {
                    message,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                throw new Error( message );
            } finally {
                context.commit( mutationTypes.SET_PARTNERED_LOCATIONS_IN_FLIGHT, { inFlight: false } );
            }
        },

        async [ actionTypes.updatePartneredLocations ]( context, { partnerList } ) {
            context.commit( mutationTypes.SET_PARTNERED_LOCATIONS_IN_FLIGHT, { inFlight: true } );
            try {
                const { data } = await InventoryApi.updateLocationPartnerList( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                    locationIdList: partnerList.map( ( partneredLocation ) => partneredLocation.id ),
                } );
                context.commit( mutationTypes.SET_PARTNERED_LOCATIONS_LIST, {
                    list: data.locations.sort( ( a, b ) => ( a.friendly > b.friendly ? 1 : -1 ) ),
                } );
            } catch ( error ) {
                const message = `An error occurred while updating partnered locations: ${ ErrorUtil.getMessageFromError( error ) }`;
                await context.dispatch( 'snackbar', {
                    message,
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
                throw new Error( ErrorUtil.getMessageFromError( error ) );
            } finally {
                context.commit( mutationTypes.SET_PARTNERED_LOCATIONS_IN_FLIGHT, { inFlight: false } );
            }
        },
    },
    mutations: {
        [ mutationTypes.SET_POSSIBLE_PARTNERS_LIST ]( state, { list } ) {
            state.possiblePartners.list = list;
        },
        [ mutationTypes.SET_POSSIBLE_PARTNERS_IN_FLIGHT ]( state, { inFlight } ) {
            state.possiblePartners.inFlight = inFlight;
        },
        [ mutationTypes.SET_PARTNERED_LOCATIONS_LIST ]( state, { list } ) {
            state.partneredLocations.list = list;
        },
        [ mutationTypes.SET_PARTNERED_LOCATIONS_IN_FLIGHT ]( state, { inFlight } ) {
            state.partneredLocations.inFlight = inFlight;
        },
    },
};
